import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PaperPlaneTilt, Warning, ClockCountdown, Plus } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AiSuggest } from "@/components/AiSuggest";

export default function Notifications() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [days, setDays] = useState(3);
  const [sending, setSending] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [custom, setCustom] = useState({ subject: "", body: "", audience: "admins", prompt: "" });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [sendingCustom, setSendingCustom] = useState(false);

  const { data: allProducts } = useQuery({
    queryKey: ["products", "notif-picker"],
    queryFn: async () => (await api.get("/products")).data,
    enabled: customOpen,
  });

  const sendCustom = async () => {
    setSendingCustom(true);
    try {
      const { data } = await api.post("/notifications/custom", {
        subject: custom.subject, body: custom.body,
        audience: custom.audience, product_ids: selectedProducts,
      });
      toast.success(data.message);
      setCustomOpen(false);
      setCustom({ subject: "", body: "", audience: "admins", prompt: "" });
      setSelectedProducts([]);
      queryClient.invalidateQueries({ queryKey: ["notifications-log"] });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSendingCustom(false);
    }
  };

  const toggleProduct = (id) =>
    setSelectedProducts((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const { data: stale } = useQuery({
    queryKey: ["stale-products", days],
    queryFn: async () => (await api.get("/notifications/stale", { params: { days } })).data,
  });
  const { data: log } = useQuery({
    queryKey: ["notifications-log"],
    queryFn: async () => (await api.get("/notifications")).data,
    enabled: can("notifications.manage"),
  });

  const sendDigest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/notifications/digest", { days: Number(days) });
      toast.success(`${data.message}${data.ai_generated ? " — content written by AI" : " — template used (AI unavailable)"}`);
      queryClient.invalidateQueries({ queryKey: ["notifications-log"] });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="notifications-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter text-slate-900">Notifications</h1>
          <p className="mt-1 text-sm text-slate-500">AI-written email digests for products waiting too long in the pipeline</p>
        </div>
        {can("notifications.manage") && (
          <Button onClick={() => setCustomOpen(true)} data-testid="custom-notification-button" variant="outline" className="border-[#EA580C] text-[#EA580C] hover:bg-[#FFF7ED]">
            <Plus size={16} className="mr-1" /> Custom Notification
          </Button>
        )}
      </div>

      <div className="rounded-md border border-slate-200 bg-white p-5" data-testid="digest-controls">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5">
            <Label>Idle threshold (days)</Label>
            <Input type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} className="w-32" data-testid="digest-days-input" />
          </div>
          {can("notifications.manage") && (
            <Button onClick={sendDigest} disabled={sending || !(stale || []).length} data-testid="send-digest-button" className="bg-[#EA580C] font-semibold text-white hover:bg-[#C2410C]">
              <PaperPlaneTilt size={16} className="mr-1.5" />
              {sending ? "Generating & sending…" : `Send AI digest (${(stale || []).length} stale)`}
            </Button>
          )}
        </div>
        <p className="mt-3 text-xs text-slate-400">The digest is written by OpenAI based on pending steps, then emailed to all users with notification permissions.</p>
      </div>

      <div className="rounded-md border border-slate-200 bg-white" data-testid="stale-list">
        <div className="border-b border-slate-200 px-5 py-4">
          <h2 className="font-heading text-lg font-bold tracking-tight text-slate-800">Stale Products ({(stale || []).length})</h2>
        </div>
        <div className="divide-y divide-slate-100">
          {(stale || []).length === 0 && <p className="px-5 py-8 text-sm text-slate-400">No stale products at this threshold.</p>}
          {(stale || []).map((p) => (
            <Link key={p.id} to={`/products/${p.id}`} data-testid={`stale-item-${p.id}`} className="block px-5 py-4 transition-colors hover:bg-slate-50">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-semibold text-slate-900">{p.name}</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-[#B91C1C]">
                  <ClockCountdown size={12} /> {p.days_stale} days idle
                </span>
              </div>
              <div className="mt-1.5 flex items-start gap-1.5 text-xs text-slate-500">
                <Warning size={14} className="mt-0.5 shrink-0 text-[#F59E0B]" />
                <span>{p.pending_steps.length} pending: {p.pending_steps.slice(0, 6).join(", ")}{p.pending_steps.length > 6 ? "…" : ""}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {can("notifications.manage") && (
        <div className="rounded-md border border-slate-200 bg-white" data-testid="notifications-log">
          <div className="border-b border-slate-200 px-5 py-4">
            <h2 className="font-heading text-lg font-bold tracking-tight text-slate-800">Sent Notifications</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {(log || []).length === 0 && <p className="px-5 py-8 text-sm text-slate-400">No notifications sent yet.</p>}
            {(log || []).map((n) => (
              <div key={n.id} className="px-5 py-4" data-testid={`notification-log-${n.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-slate-900">{n.subject}</span>
                  <div className="flex items-center gap-2">
                    {n.ai_generated && <span className="rounded-full bg-[#FFF7ED] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#C2410C]">AI</span>}
                    <span className="text-xs text-slate-400">{new Date(n.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <p className="mt-1 text-xs text-slate-500">To: {n.recipients.join(", ")} · By: {n.sent_by}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="custom-notification-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight">Custom Notification</DialogTitle>
            <DialogDescription>Compose an email to your team. Use AI to draft it from a short brief.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Audience</Label>
              <Select value={custom.audience} onValueChange={(v) => setCustom({ ...custom, audience: v })}>
                <SelectTrigger data-testid="custom-audience-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admins">Admins & notification managers</SelectItem>
                  <SelectItem value="all">All verified users</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Brief for AI draft (optional)</Label>
                <AiSuggest
                  kind="notification"
                  context={{
                    prompt: custom.prompt,
                    products: (allProducts || []).filter((p) => selectedProducts.includes(p.id)).map((p) => `${p.name} (${p.completed_steps}/${p.total_steps} steps)`).join(", "),
                  }}
                  onApply={(item) => setCustom((c) => ({ ...c, subject: item.extra?.subject || c.subject, body: item.value }))}
                  testid="ai-notification-suggest"
                />
              </div>
              <Textarea data-testid="custom-prompt-input" rows={2} value={custom.prompt} onChange={(e) => setCustom({ ...custom, prompt: e.target.value })} placeholder="e.g. Remind the team to finish costing approvals before Friday" />
            </div>
            <div className="space-y-1.5">
              <Label>Subject</Label>
              <Input data-testid="custom-subject-input" value={custom.subject} onChange={(e) => setCustom({ ...custom, subject: e.target.value })} placeholder="Email subject" />
            </div>
            <div className="space-y-1.5">
              <Label>Message</Label>
              <Textarea data-testid="custom-body-input" rows={4} value={custom.body} onChange={(e) => setCustom({ ...custom, body: e.target.value })} placeholder="Email body" />
            </div>
            <div className="space-y-1.5">
              <Label>Attach product status (optional)</Label>
              <div className="max-h-36 space-y-2 overflow-y-auto rounded-md border border-slate-200 p-3">
                {(allProducts || []).length === 0 && <p className="text-xs text-slate-400">No products</p>}
                {(allProducts || []).map((p) => (
                  <label key={p.id} className="flex items-center gap-2.5 text-sm text-slate-700" data-testid={`custom-product-${p.id}`}>
                    <Checkbox checked={selectedProducts.includes(p.id)} onCheckedChange={() => toggleProduct(p.id)} />
                    {p.name} <span className="text-xs text-slate-400">({p.completed_steps}/{p.total_steps})</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCustomOpen(false)}>Cancel</Button>
              <Button onClick={sendCustom} disabled={sendingCustom || !custom.subject.trim() || !custom.body.trim()} data-testid="custom-send-button" className="bg-[#EA580C] font-semibold text-white hover:bg-[#C2410C]">
                <PaperPlaneTilt size={16} className="mr-1.5" /> {sendingCustom ? "Sending…" : "Send"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
